import { useState, useEffect } from 'react';

/**
 * SessionTimer — Displays elapsed time during a BLS session as `mm:ss`.
 *
 * Headless-ish: renders just a `<span>` with the time. The parent supplies wrapper
 * styling (background, padding, icon, etc.) so the component composes cleanly in
 * different placements (sticky toolbar, preview strip overlay, status panel).
 *
 * Updates once per second while `isPlaying`. Resets when `isPlaying` flips false.
 * Parent should set `aria-hidden="true"` on its wrapper if a 1Hz update would be
 * announced as noise by assistive tech.
 */

interface SessionTimerProps {
  isPlaying: boolean;
  className?: string;
}

export function SessionTimer({ isPlaying, className }: SessionTimerProps): JSX.Element {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isPlaying) {
      setElapsedSeconds(0);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const mins = Math.floor(elapsedSeconds / 60);
  const secs = elapsedSeconds % 60;
  const formatted = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

  return <span className={className ?? 'font-mono text-sm font-medium'}>{formatted}</span>;
}
